/**
 * Theme Provider
 *
 * Provides theme context to the application.
 * Follows architectural principle: Framework-agnostic business logic (lib/theme.ts)
 * separated from React-specific context (this file).
 */

import { createContext, useContext, useEffect, useState, type ReactNode } from 'react'
import {
  type ThemeMode,
  type ResolvedTheme,
  storeTheme,
  resolveTheme,
  applyTheme,
  initializeTheme,
} from '../lib/theme'

interface ThemeContextValue {
  mode: ThemeMode
  resolvedTheme: ResolvedTheme
  setMode: (mode: ThemeMode) => void
}

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined)

interface ThemeProviderProps {
  children: ReactNode
  defaultTheme?: ThemeMode
  storageKey?: string
}

/**
 * Theme Provider Component
 *
 * Wraps the application to provide theme state and controls.
 * Initializes theme on mount and listens to system preference changes.
 */
export function ThemeProvider({
  children,
  defaultTheme = 'system',
  storageKey = 'theme',
}: ThemeProviderProps) {
  const [mode, setModeState] = useState<ThemeMode>(() => {
    // Initialize from storage or default
    const stored = localStorage.getItem(storageKey)
    if (stored === 'light' || stored === 'dark' || stored === 'system') {
      return stored
    }
    return defaultTheme
  })

  const [resolvedTheme, setResolvedTheme] = useState<ResolvedTheme>(() => {
    return resolveTheme(mode)
  })

  // Apply theme when mode changes
  useEffect(() => {
    const resolved = resolveTheme(mode)
    setResolvedTheme(resolved)
    applyTheme(resolved)
    storeTheme(mode)
  }, [mode, storageKey])

  // Listen to system preference changes when mode is 'system'
  useEffect(() => {
    if (mode !== 'system') return

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')

    const handleChange = () => {
      const resolved = resolveTheme('system')
      setResolvedTheme(resolved)
      applyTheme(resolved)
    }

    // Listen for changes
    mediaQuery.addEventListener('change', handleChange)

    return () => {
      mediaQuery.removeEventListener('change', handleChange)
    }
  }, [mode])

  const setMode = (newMode: ThemeMode) => {
    setModeState(newMode)
  }

  const value: ThemeContextValue = {
    mode,
    resolvedTheme,
    setMode,
  }

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  )
}

/**
 * useTheme Hook
 *
 * Provides access to theme state and controls.
 * Must be used within a ThemeProvider.
 *
 * @throws Error if used outside ThemeProvider
 *
 * @example
 * const { resolvedTheme, setMode } = useTheme()
 * return (
 *   <button onClick={() => setMode(resolvedTheme === 'dark' ? 'light' : 'dark')}>
 *     Toggle theme
 *   </button>
 * )
 */
export function useTheme(): ThemeContextValue {
  const context = useContext(ThemeContext)

  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider')
  }

  return context
}

/**
 * Initialize theme on application start (outside React)
 * Call this in main.tsx before rendering the app
 */
export { initializeTheme }
